import { useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  arcPath,
  completionFillColour,
  computeDialLayout,
  DIAL_COLOURS,
  graphemeCount,
  sectionBandFill,
  sectionLabelColour,
  radialLabelRotation,
  readableTextColour,
  type DialLayout,
  type DialModel,
} from "@keydials/dial-renderer";

/** The arc under the pointer (or keyboard focus), as reported by `onHover` and `renderTooltip`. */
export interface DialHoverTarget {
  kind: "section" | "segment";
  sectionKey: string;
  /** Present when kind is "segment". */
  segmentKey?: string;
  label: string;
  statusKey?: string;
}

export interface DialProps {
  model: DialModel;
  /** Rendered width/height in px. */
  size?: number;
  selectedSectionKey?: string | null;
  selectedSegmentKey?: string | null;
  onSelectSection?: (sectionKey: string) => void;
  onSelectSegment?: (sectionKey: string, segmentKey: string) => void;
  /**
   * The selected section, or an action point inside it, matched on both keys (KEY-560). A segment key is only unique within its
   * section, so `selectedSegmentKey` alone can light two action points at once. When given, this takes the place of
   * `selectedSectionKey` and `selectedSegmentKey`: a section selection highlights the section's band, an action point selection
   * highlights that action point.
   */
  selection?: { sectionKey: string; segmentKey?: string | null } | null;
  /** With `selection`, fade every arc outside it and halo the selected one, so what is being edited stands out. */
  dimUnselected?: boolean;
  /** What screen-reader labels call a section, e.g. the dial's own word. Default "Section". */
  sectionWord?: string;
  /** What screen-reader labels call an action point, e.g. the dial's own word. Default "Segment". */
  segmentWord?: string;
  centreLabel?: string;
  ariaLabel?: string;
  /** Maps status keys to human names for screen-reader labels (e.g. { complete: "Satisfied" }). */
  statusLabels?: Record<string, string>;
  /** Optional image rendered in the centre hub (e.g. a dial logo). */
  centreImageUrl?: string | null;
  /**
   * Renders extra SVG (badges, markers, ribbons) on top of the dial, in the dial's own
   * coordinate space. Receives the computed layout so callers can position from each arc's
   * `centroid`/angles without recomputing geometry.
   */
  overlay?: (layout: DialLayout) => ReactNode;
  /** Called with the hovered or focused arc, and with null when the pointer/focus leaves it. */
  onHover?: (target: DialHoverTarget | null) => void;
  /**
   * Opt-in cursor-tracked tooltip: rendered next to the pointer while an arc is hovered.
   * Keyboard focus is announced via the existing aria-live region instead.
   */
  renderTooltip?: (target: DialHoverTarget) => ReactNode;
  /** Fill the parent's width (keeping the 1:1 aspect) instead of a fixed pixel size. */
  fluid?: boolean;
}

const NEXT_KEYS = ["ArrowRight", "ArrowDown"];
const PREV_KEYS = ["ArrowLeft", "ArrowUp"];

const srOnly: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  border: 0,
};

export function Dial({
  model,
  size = 520,
  selectedSectionKey,
  selectedSegmentKey,
  onSelectSection,
  onSelectSegment,
  selection,
  dimUnselected = false,
  sectionWord = "Section",
  segmentWord = "Segment",
  centreLabel,
  ariaLabel = "Key dial",
  statusLabels,
  centreImageUrl,
  overlay,
  onHover,
  renderTooltip,
  fluid = false,
}: DialProps) {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [hoverTarget, setHoverTarget] = useState<DialHoverTarget | null>(null);
  const [tipPos, setTipPos] = useState<{ x: number; y: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const segmentRefs = useRef<(SVGPathElement | null)[]>([]);
  const sectionRefs = useRef<(SVGPathElement | null)[]>([]);
  const hubClipId = `hub-clip-${useId().replace(/:/g, "")}`;
  const layout = computeDialLayout(model);

  const pad = 8;
  const extent = model.outerRadius + pad;
  const showProgress = model.showCentralProgress !== false;
  const hubRadius = model.innerRadius;
  // The completion ring hugs the inner edge of the segments (a frame around the hub) rather than
  // floating as a small inner disc.
  const ringOuter = hubRadius - 3;
  const ringInner = ringOuter - 16;
  // When the ring is shown, a central image is clipped to sit inside it; otherwise it fills the hub.
  const imageRadius = showProgress ? Math.max(ringInner - 3, 8) : hubRadius;
  const completionPct = Math.round(layout.completion * 100);

  const brightness = (key: string, selected: boolean) =>
    selected ? "brightness(0.75)" : activeKey === key ? "brightness(0.88)" : undefined;

  const statusName = (statusKey?: string) =>
    statusKey ? (statusLabels?.[statusKey] ?? statusKey) : undefined;

  const capitalise = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);
  const sectionTerm = capitalise(sectionWord);
  const segmentTerm = capitalise(segmentWord);

  // A pair selection (KEY-560) matches both keys. Without one, the older props behave exactly as they always have.
  const isSegmentSelected = (sectionKey: string, segmentKey: string) =>
    selection !== undefined
      ? selection != null && selection.segmentKey != null && selection.sectionKey === sectionKey && selection.segmentKey === segmentKey
      : selectedSegmentKey === segmentKey;
  const isSectionSelected = (sectionKey: string) =>
    selection !== undefined
      ? selection != null && selection.segmentKey == null && selection.sectionKey === sectionKey
      : selectedSectionKey === sectionKey;
  // Outside the selection, when asked to dim. A section selection keeps its action points; an action point selection keeps its
  // section's band, so the reader can still see where it sits.
  const isDimmed = (sectionKey: string, segmentKey?: string) =>
    dimUnselected &&
    selection != null &&
    (selection.sectionKey !== sectionKey ||
      (segmentKey !== undefined && selection.segmentKey != null && selection.segmentKey !== segmentKey));
  const withHalo = (filter: string | undefined, selected: boolean) =>
    dimUnselected && selected ? `${filter ?? ""} drop-shadow(0 0 5px rgba(30, 29, 81, 0.55))`.trim() : filter;
  const fade = (dimmed: boolean): CSSProperties | undefined =>
    dimUnselected ? { opacity: dimmed ? 0.45 : 1, transition: "opacity 0.2s" } : undefined;

  // Hover state is only tracked when someone is listening — existing callers pay nothing.
  const trackHover = renderTooltip != null || onHover != null;
  const setHover = (target: DialHoverTarget | null) => {
    if (!trackHover) return;
    if (renderTooltip) setHoverTarget(target);
    onHover?.(target);
  };
  const moveTip = (e: { clientX: number; clientY: number }) => {
    if (!renderTooltip) return;
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (rect) setTipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  // Roving focus: arrow keys cycle through the focusable arcs of the same ring.
  const move = (refs: (SVGPathElement | null)[], index: number, delta: number) => {
    const focusable = refs.filter(Boolean) as SVGPathElement[];
    if (focusable.length === 0) return;
    const current = refs[index];
    const pos = current ? focusable.indexOf(current) : 0;
    focusable[(pos + delta + focusable.length) % focusable.length].focus();
  };

  return (
    <div
      ref={wrapperRef}
      style={
        fluid
          ? { position: "relative", width: "100%", aspectRatio: "1 / 1" }
          : { position: "relative", width: size, height: size }
      }
    >
      <svg
        width={fluid ? "100%" : size}
        height={fluid ? "100%" : size}
        viewBox={`${-extent} ${-extent} ${extent * 2} ${extent * 2}`}
        role="img"
        aria-label={`${ariaLabel} — ${completionPct}% complete`}
      >
        {/* Inner ring — segment status colours */}
        {layout.segmentArcs.map((arc, i) => {
          const key = `${arc.sectionKey}/${arc.segmentKey}`;
          const selected = isSegmentSelected(arc.sectionKey, arc.segmentKey);
          const dimmed = isDimmed(arc.sectionKey, arc.segmentKey);
          // Screen readers hear the short label, or the full name when a new action point has no label yet.
          const spoken = arc.label || arc.name || "";
          const wideEnough = (arc.endAngle - arc.startAngle) * model.midRadius > 22;
          const status = statusName(arc.statusKey);
          const hover: DialHoverTarget = {
            kind: "segment",
            sectionKey: arc.sectionKey,
            segmentKey: arc.segmentKey,
            label: arc.label,
            statusKey: arc.statusKey,
          };
          return (
            <g key={key} data-dimmed={dimmed || undefined} style={fade(dimmed)}>
              <path
                ref={(el) => {
                  segmentRefs.current[i] = el;
                }}
                d={arc.path}
                data-selected={selected || undefined}
                fill={arc.fill}
                stroke="rgba(255,255,255,0.6)"
                strokeWidth={selected ? (dimUnselected ? 3 : 2) : 0.8}
                style={{ cursor: onSelectSegment ? "pointer" : "default", filter: withHalo(brightness(key, selected), selected), outline: "none" }}
                tabIndex={onSelectSegment ? 0 : -1}
                role={onSelectSegment ? "button" : undefined}
                aria-label={`${segmentTerm} ${spoken}${status ? `, status ${status}` : ""}`}
                onMouseEnter={(e) => {
                  setActiveKey(key);
                  setHover(hover);
                  moveTip(e);
                }}
                onMouseMove={moveTip}
                onMouseLeave={() => {
                  setActiveKey(null);
                  setHover(null);
                }}
                onFocus={() => {
                  setActiveKey(key);
                  onHover?.(hover);
                  setAnnouncement(`${segmentTerm} ${spoken}${status ? `, status ${status}` : ""}`);
                }}
                onBlur={() => {
                  setActiveKey(null);
                  onHover?.(null);
                }}
                onClick={() => onSelectSegment?.(arc.sectionKey, arc.segmentKey)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelectSegment?.(arc.sectionKey, arc.segmentKey);
                  } else if (NEXT_KEYS.includes(e.key)) {
                    e.preventDefault();
                    move(segmentRefs.current, i, 1);
                  } else if (PREV_KEYS.includes(e.key)) {
                    e.preventDefault();
                    move(segmentRefs.current, i, -1);
                  }
                }}
              />
              {wideEnough && (
                <text
                  transform={`translate(${arc.centroid[0]},${arc.centroid[1]}) rotate(${radialLabelRotation(arc.startAngle, arc.endAngle)})`}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={8}
                  fill={readableTextColour(arc.fill)}
                  pointerEvents="none"
                >
                  {arc.label}
                </text>
              )}
            </g>
          );
        })}

        {/* Outer ring — section labels (brand fill, or the section's own colour — KEY-755) */}
        {layout.sectionArcs.map((arc, i) => {
          const selected = isSectionSelected(arc.sectionKey);
          const dimmed = isDimmed(arc.sectionKey);
          const hover: DialHoverTarget = { kind: "section", sectionKey: arc.sectionKey, label: arc.label };
          return (
            <g key={arc.sectionKey} data-dimmed={dimmed || undefined} style={fade(dimmed)}>
              <path
                ref={(el) => {
                  sectionRefs.current[i] = el;
                }}
                d={arc.path}
                data-selected={selected || undefined}
                // KEY-755: a section's own colour when it has one (selection then reads from the halo alone).
                fill={sectionBandFill(arc.colour, selected ? DIAL_COLOURS.sectionFillSelected : DIAL_COLOURS.sectionFill)}
                stroke="#ffffff"
                strokeWidth={1.5}
                style={{ cursor: onSelectSection ? "pointer" : "default", filter: withHalo(brightness(arc.sectionKey, selected), selected), outline: "none" }}
                tabIndex={onSelectSection ? 0 : -1}
                role={onSelectSection ? "button" : undefined}
                aria-label={`${sectionTerm} ${arc.label}`}
                onMouseEnter={(e) => {
                  setActiveKey(arc.sectionKey);
                  setHover(hover);
                  moveTip(e);
                }}
                onMouseMove={moveTip}
                onMouseLeave={() => {
                  setActiveKey(null);
                  setHover(null);
                }}
                onFocus={() => {
                  setActiveKey(arc.sectionKey);
                  onHover?.(hover);
                  setAnnouncement(`${sectionTerm} ${arc.label}`);
                }}
                onBlur={() => {
                  setActiveKey(null);
                  onHover?.(null);
                }}
                onClick={() => onSelectSection?.(arc.sectionKey)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelectSection?.(arc.sectionKey);
                  } else if (NEXT_KEYS.includes(e.key)) {
                    e.preventDefault();
                    move(sectionRefs.current, i, 1);
                  } else if (PREV_KEYS.includes(e.key)) {
                    e.preventDefault();
                    move(sectionRefs.current, i, -1);
                  }
                }}
              />
              <text
                transform={`translate(${arc.centroid[0]},${arc.centroid[1]}) rotate(${radialLabelRotation(arc.startAngle, arc.endAngle)})`}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={graphemeCount(arc.label) > 1 ? 10 : 14 /* a lone emoji is one character too (KEY-757) */}
                fontWeight={700}
                fill={sectionLabelColour(arc.colour)}
                pointerEvents="none"
              >
                {arc.label}
              </text>
            </g>
          );
        })}

        {/* Centre hub + optional logo image + (optional) completion ring */}
        {/* KEY-755: hub, track and completion fill read the theme's --dial-* tokens, falling back to the
            export palette where no theme defines them. Set as STYLE, not the fill attribute: var() is CSS. */}
        <circle r={hubRadius} style={{ fill: `var(--dial-hub, ${DIAL_COLOURS.hub})` }} />
        {centreImageUrl && (
          <>
            <clipPath id={hubClipId}>
              <circle r={imageRadius} />
            </clipPath>
            <image
              href={centreImageUrl}
              x={-imageRadius}
              y={-imageRadius}
              width={imageRadius * 2}
              height={imageRadius * 2}
              clipPath={`url(#${hubClipId})`}
              preserveAspectRatio="xMidYMid slice"
            />
          </>
        )}
        {showProgress && (
          <>
            <path d={arcPath(ringInner, ringOuter, 0, Math.PI * 2)} style={{ fill: `var(--dial-track, ${DIAL_COLOURS.ringTrack})` }} />
            {completionPct > 0 && (
              <path
                d={arcPath(ringInner, ringOuter, 0, Math.PI * 2 * layout.completion)}
                style={{ fill: completionFill(layout.completion) }}
              />
            )}
            <text textAnchor="middle" dominantBaseline="central" y={-6} fontSize={22} fontWeight={700} fill={DIAL_COLOURS.centreText}>
              {completionPct}%
            </text>
            <text textAnchor="middle" dominantBaseline="central" y={16} fontSize={9} fill={DIAL_COLOURS.centreSubText} letterSpacing={1}>
              {centreLabel ?? "COMPLETE"}
            </text>
          </>
        )}

        {/* Caller-drawn overlays (badges, markers, ribbons), positioned from the same layout. */}
        {overlay?.(layout)}
      </svg>

      {renderTooltip && hoverTarget && tipPos && (
        <div style={{ position: "absolute", left: tipPos.x + 14, top: tipPos.y + 12, pointerEvents: "none", zIndex: 10 }}>
          {renderTooltip(hoverTarget)}
        </div>
      )}

      {/* Screen-reader announcement of the focused arc + its status. */}
      <div aria-live="polite" style={srOnly}>
        {announcement}
      </div>
    </div>
  );
}

/** The completion ring's fill as a themed CSS value: --dial-complete once fully complete, --dial-progress below,
 * each falling back to the export palette's colour (which `completionFillColour` returns) — KEY-755. */
function completionFill(completion: number): string {
  const fallback = completionFillColour(completion);
  return Math.round(completion * 100) >= 100 ? `var(--dial-complete, ${fallback})` : `var(--dial-progress, ${fallback})`;
}
